import { ZslApiTemplateExecuteOutput, ZslTemplate } from "@zapier/bff";
import { trpc } from "../trpc";

export interface GenerateExecuteTemplateState {
  status: "cloning" | "creating" | "finished" | "error";
  zsl: ZslTemplate | undefined;
  createdTemplate?: ZslApiTemplateExecuteOutput | undefined;
}

export interface Input {
  id: string;
  setGenerateExecuteTemplateStatus?: (
    status: GenerateExecuteTemplateState,
  ) => void;
  folderId?: string; // used for cache invalidation in mutations
}

export const useGenerateExecuteTemplate = ({
  id,
  setGenerateExecuteTemplateStatus,
  folderId,
}: Input) => {
  const utils = trpc.useUtils();

  // Step 1: Fetch connected assets
  const { refetch: fetchConnectedAssets } =
    trpc.interface.getConnectedAssetsIds.useQuery(
      { id },
      {
        enabled: false, // Don't run automatically
        trpc: {
          context: {
            skipBatching: true,
          },
        },
      },
    );

  // Step 2: Generate template mutation
  const { mutateAsync: generateTemplate } =
    trpc.zsl.generateTemplate.useMutation();

  // Step 3: Execute template mutation
  const { mutateAsync: executeTemplate } = trpc.zsl.executeTemplate.useMutation(
    {
      onMutate: () => {
        utils.interface.list.cancel();
      },
      onSettled: () => {
        utils.interface.list.invalidate();
        if (folderId) {
          utils.asset.getHydratedFolderAssets.invalidate({ folderId });
        }
      },
    },
  );

  // Sequential duplication function
  const generateExecuteTemplate = async () => {
    try {
      setGenerateExecuteTemplateStatus?.({
        status: "cloning",
        zsl: undefined,
        createdTemplate: undefined,
      });

      // Step 1: Fetch connected assets
      const { data: connectedAssetsIds } = await fetchConnectedAssets();

      if (!connectedAssetsIds) {
        throw new Error("Failed to fetch connected assets");
      }

      // Step 2: Generate template
      const templateResult = await generateTemplate({
        id,
        connectedAssetsIds,
      });

      if (!templateResult.template) {
        throw new Error("Failed to generate template from interface");
      }
      const template = templateResult.template as ZslTemplate;

      setGenerateExecuteTemplateStatus?.({
        status: "creating",
        zsl: template,
        createdTemplate: undefined,
      });

      // Step 3: Execute template
      const executeResult = await executeTemplate({
        template,
      });

      setGenerateExecuteTemplateStatus?.({
        status: "finished",
        zsl: undefined,
        createdTemplate: executeResult,
      });
    } catch (error) {
      console.error("Generate Execute Template failed:", error);
      setGenerateExecuteTemplateStatus?.({
        status: "error",
        zsl: undefined,
        createdTemplate: undefined,
      });
      throw error;
    }
  };

  return {
    generateExecuteTemplate,
  };
};
